import { create } from 'zustand';
import type Keycloak from 'keycloak-js';

interface User {
  name: string;
  email: string;
  roles: string[];
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  setFromKeycloak: (keycloak: Keycloak) => void;
  setFromToken: (accessToken: string, refreshToken?: string) => void;
  logout: () => void;
}

function parseJwt(token: string): Record<string, unknown> {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return {};
  }
}

function extractUser(payload: Record<string, unknown>): User {
  const roles: string[] = [];
  const realmAccess = payload.realm_access as { roles?: string[] } | undefined;
  if (realmAccess?.roles) {
    roles.push(
      ...realmAccess.roles.filter(
        (r: string) => !r.startsWith('default-roles-') && r !== 'offline_access' && r !== 'uma_authorization'
      )
    );
  }
  return {
    name: (payload.name as string) || (payload.preferred_username as string) || 'User',
    email: (payload.email as string) || '',
    roles,
  };
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,

  setFromKeycloak: (keycloak: Keycloak) => {
    const payload = keycloak.tokenParsed as Record<string, unknown> | undefined;
    set({
      user: payload ? extractUser(payload) : null,
      token: keycloak.token || null,
      refreshToken: keycloak.refreshToken || null,
      isAuthenticated: true,
    });
  },

  setFromToken: (accessToken: string, refreshToken?: string) => {
    const payload = parseJwt(accessToken);
    set({
      user: extractUser(payload),
      token: accessToken,
      refreshToken: refreshToken || null,
      isAuthenticated: true,
    });
  },

  logout: () => {
    set({ user: null, token: null, refreshToken: null, isAuthenticated: false });
  },
}));
